import { Circle, CircleCheckBig, CircleDot, Clock, X } from "lucide-react";
import type { ReactNode } from "react";
import {
  getExecutionControlLabel,
  getExecutionControlState,
} from "@shared/taskLifecycle";

export type PersistedTaskStatus = "pending" | "done" | "skipped";

type Colors = {
  bar: string;
  soft: string;
  ink: string;
};

export type TaskStatusControlsProps = {
  status: PersistedTaskStatus;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  colors: Colors;
  onStatusChange: (status: PersistedTaskStatus) => void;
};

export default function TaskStatusControls({
  status,
  startedAt,
  completedAt,
  colors,
  onStatusChange,
}: TaskStatusControlsProps) {
  const executionState = getExecutionControlState({ startedAt, completedAt });
  const executionLabel = getExecutionControlLabel(executionState);
  const executionIcon = executionState === "start"
    ? <Circle />
    : executionState === "doing"
      ? <CircleDot />
      : <CircleCheckBig />;

  return (
    <div className="status-controls">
      <StatusButton
        icon={<Clock />}
        label="Pending"
        active={status === "pending"}
        onClick={() => onStatusChange("pending")}
        colors={colors}
      />
      <StatusButton
        icon={executionIcon}
        label={executionLabel}
        active={status === "done"}
        onClick={() => onStatusChange("done")}
        colors={colors}
      />
      <StatusButton
        icon={<X />}
        label="Skipped"
        active={status === "skipped"}
        onClick={() => onStatusChange("skipped")}
        colors={colors}
      />
    </div>
  );
}

function StatusButton({
  icon,
  label,
  active,
  onClick,
  colors,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  colors: Colors;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="status-button"
      style={{
        background: active
          ? colors.bar
          : `color-mix(in oklch, ${colors.bar} 10%, transparent)`,
        color: active ? colors.soft : colors.ink,
      }}
    >
      {icon}
      {label}
    </button>
  );
}
